import { useCallback, useMemo } from 'react';
import { usePage } from '@inertiajs/react';
import { MastraClient } from '@mastra/client-js';
import type { AxiosError } from 'axios';

import { ASSISTANT_RATE_LIMITED } from '@/lib/assistant/message-turn';
import http from '@/lib/http';
import type { CreditStatus, SharedData } from '@/types/shared';

const TOKEN_CACHE_RATIO = 0.8;

interface TokenCache {
  token: string;
  expiresAt: number;
}

/** Shared across hook instances (e.g. account-scoped + thread-scoped clients in site assistant). */
const tokenCache = new Map<string, TokenCache>();
const pendingTokens = new Map<string, Promise<string>>();

/**
 * Discards every cached/in-flight token so the next request mints a fresh one.
 */
export function clearMastraTokenCache(): void {
  tokenCache.clear();
  pendingTokens.clear();
}

export type MastraTokenContext = {
  actionSource?: string;
  pageId?: string;
};

interface UseMastraClientOptions {
  threadId?: string;
  tokenContext?: MastraTokenContext;
  onCreditStatus?: (creditStatus: CreditStatus) => void;
  scopes?: string[];
}

type MastraClientMethodSubset = Pick<
  MastraClient,
  | 'createMemoryThread'
  | 'getAgent'
  | 'getMemoryThread'
  | 'listMemoryThreads'
  | 'saveMessageToMemory'
>;

export interface AbortableMastraClient extends MastraClientMethodSubset {
  withAbortSignal: (abortSignal: AbortSignal) => AbortableMastraClient;
}

function buildMastraTokenCacheKey(
  accountId: string,
  threadId: string | undefined,
  tokenContext: MastraTokenContext | undefined,
  scopes: string[] | undefined,
): string {
  const contextKey = [tokenContext?.actionSource ?? '', tokenContext?.pageId ?? ''].join(':');
  const scopesKey = scopes && scopes.length > 0 ? [...scopes].sort().join(',') : 'default';

  return [threadId ?? '', accountId, contextKey, scopesKey].join(':');
}

function toTokenRequestError(error: AxiosError<{ message?: string }>): Error {
  const status = error.response?.status;
  const apiMessage = error.response?.data?.message?.trim();

  if (status === 429) {
    return new Error(ASSISTANT_RATE_LIMITED);
  }

  if (apiMessage) {
    return new Error(apiMessage);
  }

  if (error.message) {
    return new Error(error.message);
  }

  return new Error('Unable to connect to AI right now. Please try again.');
}

/**
 * Mastra client with JWT auth from `POST /api/v1/ai/tokens`.
 *
 * Uses Inertia `accountId`, optional `threadId` / `tokenContext` (`actionSource`, `pageId`), and
 * an optional `scopes` override on the request and cache key. Page version is sent per chat
 * request via `requestContext`, not JWT.
 */
export function useMastraClient(options: UseMastraClientOptions = {}) {
  const { accountId, aiServiceUrl } = usePage<SharedData>().props;
  const resolvedAiServiceUrl = aiServiceUrl?.trim();
  const { tokenContext, threadId, onCreditStatus, scopes } = options;
  const tokenActionSource = tokenContext?.actionSource;
  const tokenPageId = tokenContext?.pageId;
  // Stabilized so an inline array literal at the call site doesn't defeat memoization.
  const scopesKey = scopes && scopes.length > 0 ? [...scopes].sort().join(',') : undefined;
  const stableScopes = useMemo(() => scopesKey?.split(','), [scopesKey]);

  const getToken = useCallback(async (): Promise<string> => {
    if (!accountId) {
      throw new Error('accountId is required to fetch JWT token');
    }

    const cacheKey = buildMastraTokenCacheKey(
      accountId,
      threadId,
      {
        actionSource: tokenActionSource,
        pageId: tokenPageId,
      },
      stableScopes,
    );

    const pending = pendingTokens.get(cacheKey);
    if (pending) {
      return pending;
    }

    const cached = tokenCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.token;
    }

    const tokenPromise = (async () => {
      try {
        if (import.meta.env.DEV) {
          // biome-ignore lint/suspicious/noConsole: dev-only mastra token request debugging
          console.debug('[mastraClient] token request', {
            accountId,
            threadId,
            tokenContext: {
              actionSource: tokenActionSource ?? null,
              pageId: tokenPageId ?? null,
            },
          });
        }

        const response = await http
          .post('/api/v1/ai/tokens', {
            accountId,
            threadId,
            actionSource: tokenActionSource,
            pageId: tokenPageId,
            scopes: stableScopes,
          })
          .catch((error: AxiosError<{ message?: string }>) => {
            if (import.meta.env.DEV) {
              console.error('[mastraClient] token request failed', {
                accountId,
                threadId,
                tokenContext: {
                  actionSource: tokenActionSource ?? null,
                  pageId: tokenPageId ?? null,
                },
                status: error.response?.status,
                apiMessage: error.response?.data?.message?.trim() ?? null,
                error,
              });
            }

            throw toTokenRequestError(error);
          });

        const { token, expiresIn, creditStatus } = response.data.data as {
          token: string;
          expiresIn: number;
          creditStatus?: CreditStatus;
        };
        if (creditStatus) {
          onCreditStatus?.(creditStatus);
        }
        const cacheTtlMs = Math.round(Number(expiresIn)) * TOKEN_CACHE_RATIO * 1000;

        if (import.meta.env.DEV) {
          // biome-ignore lint/suspicious/noConsole: dev-only mastra token response debugging
          console.debug('[mastraClient] token response', {
            accountId,
            threadId,
            expiresIn,
            cacheTtlMs,
          });
        }

        if (Number.isFinite(cacheTtlMs) && cacheTtlMs > 0) {
          tokenCache.set(cacheKey, {
            token,
            expiresAt: Date.now() + cacheTtlMs,
          });
        }

        return token;
      } finally {
        pendingTokens.delete(cacheKey);
      }
    })();

    pendingTokens.set(cacheKey, tokenPromise);

    return tokenPromise;
  }, [accountId, threadId, tokenActionSource, tokenPageId, onCreditStatus, stableScopes]);

  const authenticatedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const token = await getToken();
      const headers = new Headers(init?.headers);
      headers.set('Authorization', `Bearer ${token}`);

      const response = await fetch(input, {
        ...init,
        headers,
      });

      if (import.meta.env.DEV && !response.ok) {
        let responseBody: string | null = null;
        try {
          responseBody = await response.clone().text();
        } catch {
          responseBody = null;
        }

        console.error('[mastraClient] ai request failed', {
          input: typeof input === 'string' ? input : input.toString(),
          status: response.status,
          statusText: response.statusText,
          body: responseBody,
        });
      }

      return response;
    },
    [getToken],
  );

  const createClient = useCallback(
    (abortSignal?: AbortSignal) => {
      if (!resolvedAiServiceUrl) {
        throw new Error('aiServiceUrl is required to connect to the AI service');
      }

      const client = new MastraClient({
        baseUrl: resolvedAiServiceUrl,
        fetch: authenticatedFetch,
        abortSignal,
      });

      const abortableClient: AbortableMastraClient = {
        createMemoryThread: client.createMemoryThread.bind(client),
        getAgent: client.getAgent.bind(client),
        getMemoryThread: client.getMemoryThread.bind(client),
        listMemoryThreads: client.listMemoryThreads.bind(client),
        saveMessageToMemory: client.saveMessageToMemory.bind(client),
        withAbortSignal: (nextAbortSignal: AbortSignal) => createClient(nextAbortSignal),
      };

      return abortableClient;
    },
    [resolvedAiServiceUrl, authenticatedFetch],
  );

  return useMemo(() => createClient(), [createClient]);
}
